import { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';

const DISCORD = 'https://discord.gg/yzM2p4sFb4';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ---- Countdown Timer ----
function CountdownTimer() {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const key = 'vanta_countdown_end';
    let end = localStorage.getItem(key);
    if (!end || Date.now() > parseInt(end)) {
      end = String(Date.now() + 7 * 24 * 60 * 60 * 1000);
      localStorage.setItem(key, end);
    }
    const tick = () => {
      const diff = Math.max(0, parseInt(end) - Date.now());
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex items-center gap-2 font-inter text-sm">
      {[['d', time.d], ['h', time.h], ['m', time.m], ['s', time.s]].map(([l, v]) => (
        <div key={l} className="flex flex-col items-center">
          <span className="font-outfit text-xl font-light text-white w-8 text-center tabular-nums">
            {String(v).padStart(2, '0')}
          </span>
          <span className="text-zinc-600 text-[9px] uppercase tracking-widest">{l}</span>
        </div>
      ))}
    </div>
  );
}

// ---- Live Viewer Count ----
function LiveViewers() {
  const [count, setCount] = useState(23);
  useEffect(() => {
    const id = setInterval(() => {
      setCount(prev => {
        const delta = Math.floor(Math.random() * 3) - 1;
        return Math.min(38, Math.max(16, prev + delta));
      });
    }, 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="flex items-center gap-1.5 text-xs font-inter text-zinc-500">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
      {count} {count === 1 ? 'person' : 'people'} viewing now
    </span>
  );
}

const PRODUCTS = [
  {
    id: 'free', price: 0, oldPrice: null, badge: null,
    features: {
      en: ['Basic optimizations', 'Community access', 'Discord support'],
      de: ['Grundlegende Optimierungen', 'Community-Zugang', 'Discord-Support'],
    },
  },
  {
    id: 'vanta_v1', price: 9.99, oldPrice: 14.99, badge: 'Popular',
    soldThisWeek: 31,
    features: {
      en: ['25+ optimizations', 'FPS boost package', 'Instant download', 'Email support'],
      de: ['25+ Optimierungen', 'FPS-Boost-Paket', 'Sofort-Download', 'E-Mail-Support'],
    },
  },
  {
    id: 'vanta_v2', price: 24.99, oldPrice: 39.99, badge: 'Best Value',
    soldThisWeek: 47,
    features: {
      en: ['50+ premium optimizations', 'Macro suite included', 'Priority support', 'Lifetime updates', 'Exclusive Discord role'],
      de: ['50+ Premium-Optimierungen', 'Makro-Suite inklusive', 'Priority-Support', 'Lebenslange Updates', 'Exklusive Discord-Rolle'],
    },
  },
];

const NAMES = {
  free: { en: 'Free', de: 'Kostenlos' },
  vanta_v1: { en: 'Vanta V1', de: 'Vanta V1' },
  vanta_v2: { en: 'Vanta V2', de: 'Vanta V2' },
};

function ProductCard({ product, lang, t, index }) {
  const isHighlighted = product.id === 'vanta_v2';
  const name = NAMES[product.id][lang] || NAMES[product.id].en;
  const features = product.features[lang] || product.features.en;

  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -10 }}
      className={`relative flex flex-col p-8 rounded-3xl border backdrop-blur-2xl group transition-all duration-500
        ${isHighlighted ? 'border-white/28' : 'border-white/10'}`}
      style={{
        background: isHighlighted ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.02)',
        boxShadow: isHighlighted ? '0 0 70px rgba(255,255,255,0.07)' : 'none',
      }}
    >
      <div className="absolute inset-0 rounded-3xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.07) 0%, transparent 55%)' }} />
      <div className={`absolute inset-0 rounded-3xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500
        ${isHighlighted ? 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.42)]' : 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]'}`} />

      {product.badge && (
        <motion.span
          animate={{ boxShadow: ['0 0 0px rgba(255,255,255,0)', '0 0 22px rgba(255,255,255,0.2)', '0 0 0px rgba(255,255,255,0)'] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-white text-black text-xs font-bold font-inter rounded-full tracking-wide z-10"
        >{product.badge}</motion.span>
      )}

      {product.soldThisWeek && (
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/8 rounded-full px-2.5 py-1">
          <span className="w-1 h-1 rounded-full bg-green-400" />
          <span className="font-inter text-[10px] text-zinc-400">{product.soldThisWeek} sold this week</span>
        </div>
      )}

      <div className="flex-1 relative z-10">
        <h3 className="font-outfit text-2xl font-medium text-white mb-2 text-center">{name}</h3>
        <div className="text-center mb-6">
          {product.oldPrice && (
            <div className="font-inter text-sm text-zinc-600 line-through mb-0.5">€{product.oldPrice.toFixed(2)}</div>
          )}
          {product.price === 0 ? (
            <span className="font-outfit text-5xl font-light text-white">Free</span>
          ) : (
            <div className="flex items-end justify-center gap-1">
              <span className="font-outfit text-5xl font-light text-white">€{product.price.toFixed(2)}</span>
              {product.oldPrice && (
                <span className="mb-1.5 text-xs font-inter text-green-400 font-medium">
                  -{Math.round((1 - product.price / product.oldPrice) * 100)}%
                </span>
              )}
            </div>
          )}
        </div>

        <ul className="space-y-2.5 mb-6">
          {features.map((f, i) => (
            <motion.li key={i} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.1 + i * 0.05 }}
              className="flex items-center gap-2.5 text-sm font-inter text-zinc-400"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
              {f}
            </motion.li>
          ))}
        </ul>

        {/* Trust line */}
        {product.id !== 'free' && (
          <p className="text-center font-inter text-[10px] text-zinc-700 mb-4">
            Instant delivery · Safe & reversible
          </p>
        )}
      </div>

      <motion.button
        data-testid={`buy-btn-${product.id}`}
        onClick={() => window.open(DISCORD, '_blank', 'noopener,noreferrer')}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.96 }}
        className={`relative w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-medium font-inter overflow-hidden group/btn cursor-pointer z-10
          ${isHighlighted ? 'bg-white text-black hover:shadow-[0_0_40px_rgba(255,255,255,0.3)]' : 'bg-white/8 text-white border border-white/15 hover:bg-white/15'}`}
      >
        <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 pointer-events-none" />
        <span className="relative z-10">{product.id === 'free' ? t.shop.freeBtn : t.shop.buyBtn}</span>
        <ArrowRight size={13} className="relative z-10" />
      </motion.button>
    </motion.div>
  );
}

export default function ShopSection() {
  const { lang, t } = useLanguage();

  return (
    <section id="shop" className="py-40 relative overflow-hidden">
      <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-white/25 to-transparent origin-top"
      />
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 flex flex-col items-center text-center relative z-10">
        {/* Countdown banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mb-12 flex flex-col sm:flex-row items-center gap-4 px-6 py-4 rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-xl"
        >
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 rounded-full bg-red-500"
            />
            <span className="font-inter text-xs text-zinc-400 tracking-wide uppercase">
              {lang === 'de' ? 'Launch-Preis endet in' : 'Launch pricing ends in'}
            </span>
          </div>
          <CountdownTimer />
          <LiveViewers />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} viewport={{ once: true }} className="mb-16">
          <p className="font-inter text-xs tracking-[0.3em] text-zinc-700 uppercase mb-4">Pricing</p>
          <h2 className="font-outfit text-4xl sm:text-5xl font-light tracking-tight text-white mb-3">{t.shop.heading}</h2>
          <p className="font-inter text-zinc-600 text-base">{t.shop.sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          {PRODUCTS.map((p, i) => <ProductCard key={p.id} product={p} lang={lang} t={t} index={i} />)}
        </div>

        {/* Trust badges */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-4"
        >
          {['Secure Purchase', 'Instant Delivery', 'Safe & Tested', 'Active Community'].map((b, i) => (
            <span key={i} className="flex items-center gap-1.5 font-inter text-xs text-zinc-600">
              <span className="text-white/40">✓</span> {b}
            </span>
          ))}
        </motion.div>

        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="mt-4 font-inter text-xs text-zinc-800"
        >
          All purchases handled through Discord
        </motion.p>
      </div>
    </section>
  );
}
